'use client';

import { useAuth, useUpdateProfile } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { HandleInput, type HandleStatus } from '@/components/profile/handle-input';
import { ConnectProviderButtons, type ProviderConnectResult } from '@/components/settings/connect-provider-buttons';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Separator } from '@/components/ui/app-separator';
import { InputField } from '@/components/ui/input-field';
import { Label } from '@/components/ui/label';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { useImpersonationStore } from '@/lib/impersonation';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 30;

/**
 * Bailer guard: if an authenticated user previously entered the onboarding
 * flow (so the `onboarding_started:<userId>` flag is set in localStorage)
 * but landed back in the app with a missing display name or handle, this
 * modal prompts them to complete it.
 *
 * Gated on the `onboarding_started` flag so a brand-new signup doesn't
 * flash the modal in the gap between `setUser(…)` and the
 * `router.push('/onboarding')` navigation. The flag is set by the
 * onboarding page itself the moment it mounts — so until then, this
 * modal stays out of the way and lets onboarding own the profile-capture
 * UX. After the user reaches onboarding even once, the flag is sticky
 * (per-user localStorage), and any future page load with missing fields
 * surfaces the modal.
 */
export function CompleteProfileModal(): JSX.Element | null {
  const t = useTranslations('profile.complete');
  const {
    isAuthenticated,
    user,
    setUser
  } = useAuth();
  const isImpersonating = useImpersonationStore((s): boolean => s.isActive);
  const {
    mutateAsync: updateProfile
  } = useUpdateProfile();
  const checkedRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [handleStatus, setHandleStatus] = useState<HandleStatus>('idle');
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    // Spec 048: never prompt the engineer to "complete" the impersonated
    // user's profile — the modal would write to the engineer's row via
    // the SDK and the backend rejects the write anyway. Suppress entirely
    // so no UI surfaces a write CTA the engineer cannot complete.
    if (isImpersonating) return;
    if (!isAuthenticated || user === null || checkedRef.current) return;

    // Skip until the user has actually entered the onboarding flow at
    // least once. Without this, brand-new signups see the modal flash
    // for a frame between `setUser(…)` and the route change to
    // `/onboarding`.
    let onboardingStarted = false;
    try {
      onboardingStarted = localStorage.getItem(`onboarding_started:${user.id}`) === 'true';
    } catch {
      // SILENT-CATCH: localStorage may be blocked — fall through with
      // onboardingStarted = false, which means the modal stays closed.
      // The onboarding page itself is the canonical entry point in that
      // case; we'd rather miss a bailer than flash the modal on signup.
    }
    if (!onboardingStarted) return;
    checkedRef.current = true;
    const missingName = user.displayName === null || user.displayName.trim().length === 0;
    const missingHandle = user.handle === null || user.handle.trim().length === 0;
    if (missingName || missingHandle) {
      setDisplayName(user.displayName ?? '');
      setHandle(user.handle ?? '');
      if (user.handle !== null && user.handle.trim().length > 0) {
        setHandleStatus('unique');
      }
      setOpen(true);
    }
  }, [isAuthenticated, user, isImpersonating]);
  if (!open || user === null) return null;
  const trimmedName = displayName.trim();
  const nameValid = trimmedName.length >= MIN_NAME_LENGTH && trimmedName.length <= MAX_NAME_LENGTH;
  const handleReady = handleStatus === 'unique';
  const canSave = nameValid && handleReady;
  async function handleSave(): Promise<void> {
    if (!canSave || user === null) return;
    setIsSaving(true);
    await runWithCleanup(async () => {
      try {
        const trimmedHandle = handle.trim();
        // IMPERSONATION_GUARD: useEffect above bails before opening when isImpersonating is true; defence in depth via backend 403.
        await updateProfile({
          displayName: trimmedName,
          ...(trimmedHandle.length > 0 && trimmedHandle !== (user.handle ?? '') ? {
            handle: trimmedHandle
          } : {})
        });
        setUser({
          ...user,
          displayName: trimmedName,
          handle: trimmedHandle
        });
        showToast({
          id: TOAST_IDS.PROFILE_UPDATED,
          message: t('success'),
          variant: 'success'
        });
        setOpen(false);
      } catch (err: unknown) {
        // The user-facing error toast is owned by the global MutationErrorListener
        // funnel — `useUpdateProfile` is an SDK mutation, so its failure is
        // already resolved to a translated message there. Just log for diagnostics.
        logger.warn('Complete profile save failed', {
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }, () => setIsSaving(false));
  }
  function handleProviderConnected(result: ProviderConnectResult): void {
    if (user === null) return;
    if (result.imported && result.displayName !== null && displayName.trim().length === 0) {
      setDisplayName(result.displayName);
    }
  }
  return <SilkSheet open={open} onOpenChange={setOpen} variant="detached" size="md" keyboard title={t('title')} description={t('description')}>
      <div className="flex flex-col gap-4">
        <InputField id="complete-display-name" label={t('displayName')} required value={displayName} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setDisplayName(e.target.value)} placeholder={t('displayNamePlaceholder')} maxLength={MAX_NAME_LENGTH + 1} disabled={isSaving} />

        <HandleInput value={handle} onChange={setHandle} onStatusChange={setHandleStatus} currentHandle={user.handle} required disabled={isSaving} id="complete-handle" />

        <AnimatedButton disabled={!canSave || isSaving} loading={isSaving} onClick={handleSave} className="w-full">
          {t('save')}
        </AnimatedButton>

        <Separator />

        <div className="flex flex-col gap-2">
          <Label>{t('connectAccounts')}</Label>
          <ConnectProviderButtons onConnected={handleProviderConnected} />
        </div>
      </div>
    </SilkSheet>;
}